import { Notification } from '@alliance-disposal/transport-types';
import { create } from 'zustand';
import { LINKED_NOTIFICATION_TYPES } from '../contact-form/contact-notifications-preferences-form';
// Map fields to their display names
const categoryLabels = {
    serviceChanges: 'Service Changes',
    billing: 'Billing',
    rollOffDispatch: 'Roll Off Dispatch',
};
// Map fields to their descriptions (excludes linked types that are auto-synced)
const categoryMapping = {
    serviceChanges: ['ROLL_OFF_ORDER_CONFIRMATION', 'ROLL_OFF_ORDER_CANCEL'],
    billing: [
        'ROLLOFF_INVOICE_PAYMENT',
        'WAYSTE_INVOICE_ISSUE_RECEIPT',
        'WAYSTE_INVOICE_PAYMENT_RECEIPT',
        'WAYSTE_AUTOPAY_RECEIPT',
        'WAYSTE_AUTOPAY_FAILURE',
        // Note: SUBSCRIPTION_INVOICE_CREATED and SUBSCRIPTION_INVOICE_PAYMENT_RECEIPT are auto-synced
    ],
    rollOffDispatch: ['ROLL_OFF_DRIVER_ENROUTE', 'ROLL_OFF_DAY_BEFORE_SERVICE_REMINDER', 'ROLL_OFF_DAY_OF_SERVICE_REMINDER'],
};
// Notification types that exist in CommunicationTypes but are NOT customer-contact preferences
// (e.g. hauler-facing alerts). These must never be defaulted on / submitted when creating a
// customer contact — the backend has no place to store them and rejects them.
const NON_CONTACT_NOTIFICATION_TYPES = new Set([
    'ROLL_OFF_NEW_ORDER_HAULER_ALERT',
]);
export const useNotificationStore = create((set, get) => ({
    globalEmailEnabled: false,
    globalSmsEnabled: false,
    groups: [],
    setGlobalEmail: (enabled) => {
        set((state) => {
            const updatedGroups = state.groups.map((group) => ({
                ...group,
                emailEnabled: enabled,
                items: group.items.map((item) => ({
                    ...item,
                    emailEnabled: enabled,
                })),
            }));
            return {
                globalEmailEnabled: enabled,
                groups: updatedGroups,
            };
        });
    },
    setGlobalSms: (enabled) => {
        set((state) => {
            const updatedGroups = state.groups.map((group) => ({
                ...group,
                smsEnabled: enabled,
                items: group.items.map((item) => ({
                    ...item,
                    smsEnabled: enabled,
                })),
            }));
            return {
                globalSmsEnabled: enabled,
                groups: updatedGroups,
            };
        });
    },
    updateItem: (groupId, itemId, updates) => {
        set((state) => {
            const allUpdatedItems = [];
            const updatedGroups = state.groups.map((group) => {
                if (group.id !== groupId) {
                    allUpdatedItems.push(...group.items);
                    return group;
                }
                const updatedGroupItems = group.items.map((item) => (item.id === itemId ? { ...item, ...updates } : item));
                allUpdatedItems.push(...updatedGroupItems);
                return {
                    ...group,
                    items: updatedGroupItems,
                    emailEnabled: updatedGroupItems.some((item) => item.emailEnabled),
                    smsEnabled: updatedGroupItems.some((item) => item.smsEnabled),
                };
            });
            const allEmailEnabled = allUpdatedItems.some((item) => item.emailEnabled);
            const allSmsEnabled = allUpdatedItems.some((item) => item.smsEnabled);
            return { groups: updatedGroups, globalEmailEnabled: allEmailEnabled, globalSmsEnabled: allSmsEnabled };
        });
    },
    updateGroup: (groupId, updates) => {
        set((state) => {
            const allUpdatedItems = [];
            const updatedGroups = state.groups.map((group) => {
                if (group.id !== groupId) {
                    allUpdatedItems.push(...group.items);
                    return group;
                }
                const updatedItems = group.items.map((item) => ({
                    ...item,
                    emailEnabled: updates.emailEnabled ?? item.emailEnabled,
                    smsEnabled: updates.smsEnabled ?? item.smsEnabled,
                }));
                allUpdatedItems.push(...updatedItems);
                return {
                    ...group,
                    ...updates,
                    items: updatedItems,
                    emailEnabled: updatedItems.some((item) => item.emailEnabled),
                    smsEnabled: updatedItems.some((item) => item.smsEnabled),
                };
            });
            const allEmailEnabled = allUpdatedItems.some((item) => item.emailEnabled);
            const allSmsEnabled = allUpdatedItems.some((item) => item.smsEnabled);
            return { groups: updatedGroups, globalEmailEnabled: allEmailEnabled, globalSmsEnabled: allSmsEnabled };
        });
    },
    retrieveCommunicationTypeMap: () => {
        // Initialize with all customer-contact notification types set to complete arrays.
        // Hauler-facing / non-contact types (NON_CONTACT_NOTIFICATION_TYPES) are excluded so they
        // are never submitted as a customer-contact preference.
        const preferences = Object.values(Notification.CommunicationTypes).reduce((acc, type) => {
            if (NON_CONTACT_NOTIFICATION_TYPES.has(type)) {
                return acc;
            }
            acc[type] = ['EMAIL', 'SMS'];
            return acc;
        }, {});
        // Get all items from groups
        const items = get().groups.flatMap((group) => group.items);
        // Update preferences for each item, keeping empty arrays for inactive types
        items.forEach((item) => {
            const type = item.id;
            const methods = [item.emailEnabled ? 'EMAIL' : undefined, item.smsEnabled ? 'SMS' : undefined].filter(Boolean);
            preferences[type] = methods;
            // Sync linked types with the same preferences
            const linkedType = LINKED_NOTIFICATION_TYPES[type];
            if (linkedType) {
                preferences[linkedType] = methods;
            }
        });
        return preferences;
    },
    initializeFromPreferences: (prefs) => {
        // Create groups regardless of whether prefs exists
        let allEmailEnabled = false;
        let allSmsEnabled = false;
        const allUpdatedItems = [];
        const groups = Object.entries(categoryMapping).map(([category, types]) => {
            const groupItems = types.map((type) => {
                const item = {
                    id: type,
                    name: Notification.CommunicationTypesLabels[type],
                    // If prefs is undefined, default to false
                    emailEnabled: prefs?.[type]?.includes('EMAIL') ?? false,
                    smsEnabled: prefs?.[type]?.includes('SMS') ?? false,
                };
                return item;
            });
            const group = {
                id: category,
                name: categoryLabels[category],
                emailEnabled: groupItems.some((item) => item.emailEnabled),
                smsEnabled: groupItems.some((item) => item.smsEnabled),
                items: groupItems,
            };
            allUpdatedItems.push(...groupItems);
            return group;
        });
        allEmailEnabled = allUpdatedItems.some((item) => item.emailEnabled);
        allSmsEnabled = allUpdatedItems.some((item) => item.smsEnabled);
        set({ groups, globalEmailEnabled: allEmailEnabled, globalSmsEnabled: allSmsEnabled });
    },
}));
//# sourceMappingURL=contact-grid-notification-store.js.map